import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { saveAs } from 'file-saver';
import { Container, Row, Col } from 'reactstrap';
import { OFFICIAL_REPORT } from '../../../constants/reportLayouts';

import {
  downloadChart,
  downloadReport,
  downloadCsv,
  downloadTxt,
  emailReport,
} from '../../../actions/tests/apiRequests';
import { notifyError, notifyInfo } from '../../../utils/notifier';
import Card from '../../shared/Card';
import InfoField from '../../shared/InfoField';
import TagsInput from '../../shared/TagsInput';
import Select from '../../shared/Select';
import { validateEmail } from '../../../utils/validators';
import styles from './styles.scss';

function ExportTestCard({
  defaultEmailTo,
  defaultEmailCc,
  hasData,
  isTestCompleted,
  disableOfficialReport,
  disableQCReport,
  reportLayout,
  reportLayoutOptions,
  onChangeReportLayout,
  layoutId,
  testId,
  hasErrors,
}) {
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [emailTo, setEmailTo] = useState(defaultEmailTo);
  const [emailCc, setEmailCc] = useState(defaultEmailCc);

  useEffect(() => {
    setEmailTo(defaultEmailTo);
    setEmailCc(defaultEmailCc);
  }, [defaultEmailTo, defaultEmailCc]);

  const addEmailRecipients = (emailTo) => {
    setEmailTo(emailTo);
  };

  const addEmailCc = (emailCc) => {
    setEmailCc(emailCc);
  };

  const exportPdf = async () => {
    setDownloadingPdf(true);

    try {
      const blob = await downloadReport(testId, reportLayout);
      saveAs(blob, 'report.pdf');
    } catch (e) {
      notifyError();
    }
    return setDownloadingPdf(false);
  };

  const exportChart = async () => {
    try {
      const blob = await downloadChart(testId);
      return saveAs(blob, 'chart.png');
    } catch (e) {
      notifyError();
    }
  };

  const exportCsv = async () => {
    try {
      const blob = await downloadCsv(testId);
      return saveAs(blob, 'report.csv');
    } catch (e) {
      return notifyError();
    }
  };

  const exportTxt = async () => {
    try {
      const blob = await downloadTxt(testId);
      return saveAs(blob, 'report.txt');
    } catch (e) {
      return notifyError();
    }
  };

  const sendEmail = async () => {
    setSendingEmail(true);

    try {
      await emailReport(
        testId,
        emailTo.map(({ value }) => value),
        emailCc.map(({ value }) => value),
        reportLayout,
        layoutId,
      );

      notifyInfo('Email report has been sent');
    } catch (e) {
      notifyError();
    } finally {
      setSendingEmail(false);
    }
  };

  const isExportDisabled = !hasData || !isTestCompleted;

  const disableReport = () => {
    if (reportLayout === OFFICIAL_REPORT) {
      return (
        disableOfficialReport || disableQCReport || downloadingPdf || !reportLayout || isExportDisabled || hasErrors
      );
    }
    return disableQCReport || downloadingPdf || !reportLayout || isExportDisabled || hasErrors;
  };

  return (
    <Card header="Exports">
      <Container>
        <Row className="mb-3">
          <Col>
            <InfoField editMode={true} label="Report Layout">
              <Select value={reportLayout} options={reportLayoutOptions} onChange={onChangeReportLayout} />
            </InfoField>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3} lg={3} className="mb-2">
            <button
              className={`btn w-100 ${styles.exportButton}`}
              onClick={() => exportChart()}
              disabled={isExportDisabled}
            >
              <i className={`fa mr-1 ${styles.chartIcon}`} />
              Export chart
            </button>
          </Col>
          <Col md={3} lg={3} className="mb-2">
            <button
              className={`btn w-100 ${styles.exportButton}`}
              onClick={() => exportCsv()}
              disabled={isExportDisabled}
            >
              <i className={`fa mr-1 ${styles.csvIcon}`} />
              Export CSV
            </button>
          </Col>
          <Col md={3} lg={3} className="mb-2">
            <button
              className={`btn w-100 ${styles.exportButton}`}
              onClick={() => exportTxt()}
              disabled={isExportDisabled}
            >
              <i className={`fa mr-1 ${styles.txtIcon}`} />
              Export TXT
            </button>
          </Col>
          <Col md={3} lg={3} className="mb-2">
            <button
              className={`btn w-100 ${styles.exportButton}`}
              onClick={() => exportPdf()}
              disabled={disableReport()}
            >
              {downloadingPdf ? (
                <i className="fa fa-spin fa-spinner" />
              ) : (
                <span>
                  <i className={`fa mr-1 ${styles.pdfIcon}`} /> Export PDF
                </span>
              )}
            </button>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <InfoField editMode={true} label="Email To">
              <TagsInput
                placeholder="Enter up to 5 emails"
                validator={validateEmail}
                value={emailTo}
                onChange={addEmailRecipients}
              />
            </InfoField>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <InfoField editMode={true} label="Email CC">
              <TagsInput
                placeholder="Enter up to 5 emails"
                validator={validateEmail}
                value={emailCc}
                onChange={addEmailCc}
              />
            </InfoField>
          </Col>
        </Row>
        <Row>
          <Col md={6} lg={8} />
          <Col md={6} lg={4} className="text-right">
            <button className={`btn w-100 ${styles.exportButton}`} onClick={sendEmail} disabled={disableReport()}>
              {sendingEmail ? (
                <i className="fa fa-spin fa-spinner" />
              ) : (
                <span>
                  <i className="fa mr-1 fa-envelope" /> Email Report
                </span>
              )}
            </button>
          </Col>
        </Row>
      </Container>
    </Card>
  );
}

ExportTestCard.propTypes = {
  testId: PropTypes.string,
  defaultEmailTo: PropTypes.array,
  defaultEmailCc: PropTypes.array,
  hasData: PropTypes.bool,
  hasErrors: PropTypes.bool,
  isTestCompleted: PropTypes.bool,
  layoutId: PropTypes.string,
  disableOfficialReport: PropTypes.bool,
  disableQCReport: PropTypes.bool,
  reportLayout: PropTypes.string,
  reportLayoutOptions: PropTypes.array,
  onChangeReportLayout: PropTypes.func,
};

export default ExportTestCard;



// WEBPACK FOOTER //
// src/components/testDetails/ExportTestCard/index.jsx